import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Role } from 'src/app/models/role.model';
import { List } from 'src/app/models/list.model';
import { RoleService } from '../../services/apis/role.service';
import {
  LoadRoles,
  LoadRolesSuccess,
  UpdateRole,
  CreateRole,
  RemoveRole,
  UpdateRoleSuccess,
  CreateRoleSuccess,
} from '../actions/role.actions';
import { switchMap, tap } from 'rxjs/operators';

export interface RoleStateModel {
  roles: Role[];
  loaded: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  pageNumber: number;
  totalCount: number;
  totalPages: number;
}

@State<RoleStateModel>({
  name: 'role',
  defaults: {
    roles: [],
    loaded: false,
    hasNextPage: false,
    hasPreviousPage: false,
    pageNumber: 0,
    totalCount: 0,
    totalPages: 0,
  },
})
@Injectable()
export class RoleState {
  constructor(private roleService: RoleService) {}

  // Selectors
  @Selector()
  static roles(state: RoleStateModel): Role[] {
    return state.roles;
  }

  @Selector()
  static loaded(state: RoleStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static roleByName(name: string) {
    return (state: RoleStateModel) =>
      state.roles.find((role) => role.name.toLowerCase() === name);
  }

  // Actions
  @Action(LoadRoles)
  loadRoles(ctx: StateContext<RoleStateModel>) {
    return this.roleService.getRolesList().pipe(
      tap((list: Role[]) => {
        ctx.dispatch(new LoadRolesSuccess(list));
      })
    );
  }

  @Action(LoadRolesSuccess)
  loadRolesSuccess(
    ctx: StateContext<RoleStateModel>,
    action: LoadRolesSuccess
  ) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      roles: action.list,
      loaded: true,
    });
  }

  @Action(UpdateRole)
  updateRole(ctx: StateContext<RoleStateModel>, action: UpdateRole) {
    return this.roleService.updateRole(action.role).pipe(
      tap(() => {
        ctx.dispatch(new UpdateRoleSuccess(action.role));
      })
    );
  }

  @Action(UpdateRoleSuccess)
  updateRoleSuccess(
    ctx: StateContext<RoleStateModel>,
    action: UpdateRoleSuccess
  ) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      roles: state.roles.map((role) =>
        role.name === action.role.name ? { ...role, ...action.role } : role
      ),
    });
  }

  @Action(CreateRole)
  createRole(ctx: StateContext<RoleStateModel>, action: CreateRole) {
    return this.roleService.createRole(action.role).pipe(
      tap(() => {
        ctx.dispatch(new CreateRoleSuccess(action.role));
      })
    );
  }
  @Action(CreateRoleSuccess)
  createRoleSuccess(
    ctx: StateContext<RoleStateModel>,
    action: CreateRoleSuccess
  ) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      roles: [...state.roles, action.role],
    });
  }

  @Action(RemoveRole)
  removeRole(ctx: StateContext<RoleStateModel>, action: RemoveRole) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      roles: state.roles.filter((role) => role.name !== action.role.name),
    });
  }
}
